/* eslint @typescript-eslint/explicit-module-boundary-types: 0 */
import { computed, inject } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { combineLatest, filter, forkJoin, pipe, switchMap, take, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
	Transport,
	TransportAction,
	TransportKind,
	TransportMapTerm,
	TransportStop,
	TransportTerm,
} from '@/new-transport/_models/transport.model';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { getTermsFromTransport } from '@/new-transport/_utils/get-terms-from-transport/get-terms-from-transport';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { TransportApiService } from '@/new-transport/_api/transport.api.service';
import {
	readPropertyFromUrlParams,
	TransportParamsOptions,
} from '@/new-transport/_utils/read-side-from-url-params/read-property-from-url-params';
import { getNumericValuesFromURLParams } from '@/new-transport/_utils/get-numeric-values-from-url-params/get-numeric-values-from-url-params';
import { withLoadingFeature } from '@/_signal-store-features/loading.feature.store';

type State = {
	transport: Transport;
	terms: TransportMapTerm[];
	transportAction: TransportAction;
	transportsToLinkTerms: TransportMapTerm[];
	transportsToLink: Transport[];
};

const initialState: State = {
	transport: null,
	terms: [],
	transportAction: TransportAction.EDIT,
	transportsToLinkTerms: [],
	transportsToLink: [],
};

export function withBasicTransport() {
	return signalStoreFeature(
		withState(initialState),
		withLoadingFeature({
			transportToLinkIsLoading: false,
			transportIsLoading: false,
			termsIsLoading: false,
		}),
		withComputed(({ transportsToLink, transportsToLinkTerms }) => ({
			transportsToLinkNames: computed(() => transportsToLink().map((v) => v.name)),
			isLinkedTransport: computed(() => !!(transportsToLink().length && transportsToLinkTerms().length)),
		})),
		withMethods((store, apiService = inject(TransportApiService)) => ({
			setTransportAction(action: TransportAction) {
				patchState(store, (state) => ({ ...state, transportAction: action }));
			},
			setNewTransport(transport: Transport) {
				patchState(store, (state) => ({ ...state, transport }));
			},
			changeTransportName(name: string) {
				patchState(store, (state) => ({ ...state, transport: { ...state.transport, name } }));
			},
			fetchTerms: rxMethod<number[]>(
				pipe(
					tap(() => store.changeLoadingState('terms', true)),
					switchMap((terms) =>
						forkJoin(terms.map((id) => apiService.getTerm(id))).pipe(
							tapResponse({
								next: (result: TransportTerm[]) => {
									const mappedTerms: TransportMapTerm[] = result.map((term) => ({
										id: term.id,
										name: term.name,
										transportStop: term.transportStop,
									}));

									const termsWithoutDuplicates = removeDuplicatesFromArray([
										...store.terms(),
										...mappedTerms,
									]);

									patchState(store, (state) => ({ ...state, terms: termsWithoutDuplicates }));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu terminiów');
								},
								finalize: () => store.changeLoadingState('terms', false),
							}),
						),
					),
				),
			),
			fetchTransport: rxMethod<number>(
				pipe(
					tap(() => store.changeLoadingState('transport', true)),
					switchMap((id) =>
						apiService.getTransport(id).pipe(
							tapResponse({
								next: (result) => {
									const terms = getTermsFromTransport(result);

									patchState(store, (state) => ({
										...state,
										transport: result,
										terms: terms,
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu transportu');
								},
								finalize: () => store.changeLoadingState('transport', false),
							}),
						),
					),
				),
			),
			fetchTransportsToLink: rxMethod<number[]>(
				pipe(
					tap(() => store.changeLoadingState('transportsToLink', true)),
					switchMap((ids) =>
						forkJoin(ids.map((id) => apiService.getTransport(id))).pipe(
							tapResponse({
								next: (result) => {
									const terms = result.map((transport) => getTermsFromTransport(transport)).flat();

									patchState(store, (state) => ({
										...state,
										transportsToLink: result,
										transportsToLinkTerms: terms,
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu transportów do połączenia');
								},
								finalize: () => store.changeLoadingState('transportsToLink', false),
							}),
						),
					),
				),
			),
			fetchTemplateStops: rxMethod<void>(
				pipe(
					tap(() => store.changeLoadingState('templateStops', true)),
					switchMap(() =>
						apiService.getTemplateStops().pipe(
							tapResponse({
								next: (response: TransportStop[]) => {
									const mappedTerms: TransportMapTerm[] = response.map((stop) => ({
										id: null,
										name: 'Szablonowy Program',
										transportStop: { ...stop, address: 'Szablonowy Program' },
									}));

									const termsWithoutDuplicates = removeDuplicatesFromArray([
										...store.terms(),
										...mappedTerms,
									]);

									patchState(store, (state) => ({ ...state, terms: termsWithoutDuplicates }));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu przystanków szablonowych');
								},
								finalize: () => store.changeLoadingState('templateStops', false),
							}),
						),
					),
				),
			),
		})),
		withHooks({
			onInit(store, activatedRoute = inject(ActivatedRoute)) {
				toObservable(store.transport)
					.pipe(filter(Boolean), take(1))
					.subscribe((transport) => {
						if (transport.kind === TransportKind.TEMPLATE) {
							store.fetchTemplateStops();
						}
					});

				combineLatest([activatedRoute.queryParams, activatedRoute.params])
					.pipe(takeUntilDestroyed())
					.subscribe(([queryParams, params]) => {
						const transportId: number = params['id'];

						if (transportId) {
							store.fetchTransport(transportId);
						} else {
							// transport id is not provided, so we're creating a new transport or linking existing ones
							store.setTransportAction(TransportAction.ADD);
							const terms = getNumericValuesFromURLParams(queryParams, 'terms');
							const side = readPropertyFromUrlParams(queryParams, TransportParamsOptions.SIDE);
							const kind = readPropertyFromUrlParams(queryParams, TransportParamsOptions.KIND);

							const newTransport = {
								// will gradually fill this object in future
								side,
							} as Transport;

							if (kind) {
								newTransport.kind = kind as unknown as TransportKind;
							}

							if (newTransport.kind === TransportKind.TEMPLATE) {
								store.fetchTemplateStops();
							} else {
								store.fetchTerms(terms);
							}

							store.setNewTransport(newTransport);
						}

						const transportsToLink = getNumericValuesFromURLParams(queryParams, 'transports');
						if (transportsToLink.length) {
							store.fetchTransportsToLink(transportsToLink);
						}
					});
			},
		}),
	);
}
